import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';

export default function Hero() {
  const videoRef = useRef(null);
  const [endTime, setEndTime] = useState(0);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setEndTime(Math.max(0, videoRef.current.duration - 2));
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && endTime > 0) {
      if (videoRef.current.currentTime >= endTime) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
    }
  };

  const scrollToRsvp = (e) => {
    e.preventDefault();
    const element = document.getElementById('rsvp');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-auto md:min-h-screen bg-aged-ivory flex flex-col items-center justify-center px-4 pt-12 pb-10 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden h-full w-full">
        <video 
          ref={videoRef}
          autoPlay 
          muted 
          loop 
          playsInline 
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          className="w-full h-full object-cover opacity-[0.15] grayscale-[30%]"
        >
          <source src="https://ik.imagekit.io/aeu02xvjgn/Midsummer/HRNDZ3271.mp4?updatedAt=1782321519119" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-aged-ivory/40"></div>
      </div>

      <div className="absolute top-0 left-0 w-full h-full bg-floral-gradient pointer-events-none opacity-60 z-1"></div>

      <div className="relative z-20 text-center mb-6 md:mb-12 max-w-5xl">
        <motion.span 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-sans text-[9px] md:text-xs uppercase tracking-[0.5em] text-estate-gold block mb-3 md:mb-6"
        >
          COMPLIMENTARY ADMISSION • RSVP REQUIRED
        </motion.span>
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="font-serif text-3xl md:text-7xl lg:text-8xl text-burgundy leading-[1.1] md:leading-[1] mb-6 md:mb-8 px-2"
        >
          Experience Your Wedding at Padua <br className="hidden md:block"/> 
          <span className="italic">Before You Book It</span>
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 0.8 }}
          className="font-sans font-light text-[12px] md:text-lg text-charcoal/70 tracking-wide max-w-3xl mx-auto leading-relaxed px-6 mb-6 md:mb-10"
        >
          Tour the estate, meet trusted wedding professionals, enjoy curated tastings, and discover what your celebration could feel like at The Padua Midsummer Affair.
        </motion.p>
        
        <motion.div
           initial={{ opacity: 0, y: 10 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: 1, duration: 1 }}
           className="font-sans text-[10px] md:text-sm font-medium text-burgundy tracking-[0.2em] mb-8"
        >
          SUNDAY, AUGUST 23 • 11:00 AM–3:00 PM • CLAREMONT, CALIFORNIA
        </motion.div>
      </div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.8, ease: "easeOut" }}
        className="w-full max-w-2xl relative z-10 flex flex-col items-center px-4"
      >
        <div className="relative group flex justify-center w-full">
          <div className="absolute inset-0 bg-estate-gold/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
          <img 
            src="https://media-manager-c.questera.ai/greta-media/8c3dc8994f71a85ad2d71f226c89cc7edce114857865647a461869cb9a6c120ffe2f6c628544606d61dcf48135de6ac8/images/aW1hZ2UvcG5n/b7605246e29441b4bd1899f02aa5869f.png" 
            alt="The Padua Midsummer Affair Invitation" 
            className="w-[85vw] md:w-full h-auto max-h-[50vh] md:max-h-none object-contain invitation-shadow rounded-sm border-[0.5px] border-estate-gold/20 relative z-10"
          />
        </div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="mt-8 md:mt-20 z-20 text-center"
      >
        <a 
          href="#rsvp" 
          onClick={scrollToRsvp}
          className="group bg-burgundy text-ivory font-sans text-[10px] md:text-xs uppercase tracking-[0.4em] px-10 md:px-16 py-5 md:py-7 hover:bg-floral-coral transition-all duration-700 shadow-2xl relative overflow-hidden block cursor-pointer mb-4"
        >
          <span className="relative z-10 uppercase">Claim Your Complimentary Invitation</span>
          <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
        </a>
        <p className="font-sans text-[9px] md:text-[11px] text-charcoal/50 tracking-wider">
          Complimentary admission for engaged couples and their guests.
        </p>
      </motion.div>
    </section>
  );
}